import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@scripts/react/components/Button";
import { useFlowState } from "@/hooks/useFlowState";
import {
    useModelPageState,
    useModelChangeError,
    useCurrentModelChange,
    useCurrentRapidData
} from "@/store";
import { DeploySteps, Flows } from "@/types/flows";
import { useModelPage } from "./hooks/useModelPage";
import { ModelPageState } from "./types";
import { ModelComparisonView } from "./ModelComparisonView";
import { ModelEmptyState, ModelErrorState } from "./components/ModelEmptyState";
import LoadingScreen from "@/components/LoadingScreen";

export function ModelModal() {
    const navigate = useNavigate();
    const { computeUrlPath } = useFlowState();

    useModelPage();

    const pageState = useModelPageState();
    const error = useModelChangeError();
    const currentChange = useCurrentModelChange();
    const rapidData = useCurrentRapidData();

    const handleClose = useCallback(() => {
        const deployPath = computeUrlPath({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY });
        navigate(deployPath);
    }, [navigate, computeUrlPath]);

    const renderContent = () => {
        if (error) {
            return (
                <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                        <p className="text-red-600">Error: {error}</p>
                    </div>
                </div>
            );
        }

        switch (pageState) {
            case ModelPageState.LOADING:
                // If rapidData has loaded but there's no model, show empty state
                if (rapidData && !currentChange) {
                    return <ModelEmptyState />;
                }
                return <LoadingScreen message="Loading model results..." />;

            case ModelPageState.NEW_MODEL_COMPARISON:
            case ModelPageState.SINGLE_MODEL_PERFORMANCE:
            case ModelPageState.HISTORICAL_COMPARISON:
                return <ModelComparisonView />;

            case ModelPageState.ERROR:
                return <ModelErrorState />;

            default:
                return <LoadingScreen message="Loading..." />;
        }
    };

    return (
        <div className="flex h-full w-full flex-col bg-white">
            {/* Header */}
            <div className="mt-4 flex items-center justify-between bg-white px-6">
                <div className="flex items-center gap-2">
                    <h1 className="text-base font-semibold text-gray-900">Model Overview</h1>
                    <span className="text-sm text-gray-500">
                        View your trained model&apos;s performance and metrics.
                    </span>
                </div>
                <Button type="secondary" size="xsmall" onClick={handleClose}>
                    <i className="far fa-times"></i>
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">{renderContent()}</div>
        </div>
    );
}
